/**
 * Journal text contrast (WCAG 1.4.3) for merchant article HTML.
 * Articles ship their own embedded designs (brand orange, light greys on white, dark greys on near-black) that often
 * sit below 4.5:1 (3:1 for large text). For every text element in `.blog-post-content` this measures the real
 * foreground/background pair and, only where it fails, moves the text colour along the same hue to the nearest colour
 * that passes. Elements on gradients or images (background cannot be known) and elements that already pass are untouched.
 * Runs once after load, never changes layout, and is skipped when the visitor prefers forced colours.
 */
(() => {
  const root = document.querySelector('.blog-post-content');
  if (!root || window.matchMedia('(forced-colors: active)').matches) return;

  const parse = (value) => {
    const srgb = value.match(/color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+%?))?\)/);
    if (srgb) {
      const a = srgb[4] === undefined ? 1 : srgb[4].endsWith('%') ? parseFloat(srgb[4]) / 100 : parseFloat(srgb[4]);
      return { r: srgb[1] * 255, g: srgb[2] * 255, b: srgb[3] * 255, a };
    }
    const m = value.match(/rgba?\(([^)]+)\)/);
    if (!m) return null;
    const p = m[1].split(/[,\s/]+/).filter(Boolean).map(Number);
    return { r: p[0], g: p[1], b: p[2], a: p.length > 3 ? p[3] : 1 };
  };
  const over = (top, bottom) => {
    const a = top.a + bottom.a * (1 - top.a);
    if (a === 0) return { r: 0, g: 0, b: 0, a: 0 };
    const mix = (t, b) => (t * top.a + b * bottom.a * (1 - top.a)) / a;
    return { r: mix(top.r, bottom.r), g: mix(top.g, bottom.g), b: mix(top.b, bottom.b), a };
  };
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const lum = (c) => 0.2126 * lin(c.r) + 0.7152 * lin(c.g) + 0.0722 * lin(c.b);
  const ratio = (a, b) => {
    const [hi, lo] = lum(a) > lum(b) ? [a, b] : [b, a];
    return (lum(hi) + 0.05) / (lum(lo) + 0.05);
  };
  const toHsl = ({ r, g, b }) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2, d = max - min;
    if (!d) return [0, 0, l];
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    return [h * 60, s, l];
  };
  const fromHsl = (h, s, l) => {
    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) => Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));
    return { r: f(0), g: f(8), b: f(4), a: 1 };
  };

  // Colour stops of a pure CSS gradient (null for url() images or gradients without readable colours).
  const stops = (image) => {
    if (/url\(/.test(image)) return null;
    const found = image.match(/rgba?\([^)]+\)/g);
    return found ? found.map(parse).filter((c) => c && c.a > 0.5) : null;
  };
  // Effective background: first opaque layer going up; null when an image is involved. A gradient yields its stops.
  const gradientStops = (el) => {
    for (let n = el; n && n.nodeType === 1; n = n.parentElement) {
      if (!paintsUnder(n, el)) continue;
      const cs = getComputedStyle(n);
      if (cs.backgroundImage !== 'none') return stops(cs.backgroundImage);
      const c = parse(cs.backgroundColor);
      if (c && c.a === 1) return null;
    }
    return null;
  };
  // A layer only counts when it is actually painted behind the text: margin collapsing or overflow can push text out of
  // the box whose background it was assumed to sit on.
  const paintsUnder = (n, el) => {
    if (n === el) return true;
    const o = getComputedStyle(n);
    if (o.overflowX !== 'visible' || o.overflowY !== 'visible') return true; // clipped or scrolled content still sits on this surface
    const r = el.getBoundingClientRect();
    const b = n.getBoundingClientRect();
    const x = r.left + r.width / 2, y = r.top + r.height / 2;
    return x >= b.left && x <= b.right && y >= b.top && y <= b.bottom;
  };
  const background = (el) => {
    let layers = [];
    for (let n = el; n && n.nodeType === 1; n = n.parentElement) {
      const cs = getComputedStyle(n);
      if (!paintsUnder(n, el)) continue;
      if (cs.backgroundImage !== 'none') return null;
      const c = parse(cs.backgroundColor);
      if (c && c.a > 0) {
        layers.push(c);
        if (c.a === 1) break;
      }
    }
    let base = { r: 255, g: 255, b: 255, a: 1 };
    const page = parse(getComputedStyle(document.body).backgroundColor);
    if (page && page.a === 1) base = page;
    for (let i = layers.length - 1; i >= 0; i--) base = over(layers[i], base);
    return base;
  };
  const backgroundElement = (el) => {
    for (let n = el; n && n.nodeType === 1; n = n.parentElement) {
      if (!paintsUnder(n, el)) continue;
      const c = parse(getComputedStyle(n).backgroundColor);
      if (c && c.a === 1 && getComputedStyle(n).backgroundImage === 'none') return n;
    }
    return null;
  };
  const opacity = (el) => {
    let o = 1;
    for (let n = el; n && n.nodeType === 1 && n !== document.body; n = n.parentElement) o *= parseFloat(getComputedStyle(n).opacity) || 0;
    return o;
  };

  const fix = (el) => {
    const cs = getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.display === 'none') return false;
    const fgRaw = parse(cs.color);
    let bg = background(el);
    const grad = !bg && gradientStops(el);
    if (!fgRaw || (!bg && !(grad && grad.length))) return false;
    if (!bg) {
      // Gradient: the stop closest to the text colour is the worst case; every other stop is checked too.
      const worst = (c) => Math.min(...grad.map((s) => ratio(c, s)));
      const size0 = parseFloat(cs.fontSize);
      const need0 = size0 >= 24 || (size0 >= 18.66 && parseInt(cs.fontWeight, 10) >= 700) ? 3 : 4.5;
      if (worst(fgRaw) >= need0) return false;
      const [h0, s0, l0] = toHsl(fgRaw);
      let found = null;
      for (let i = 0; i <= 40 && !found; i++) {
        for (const l of [l0 + i / 40, l0 - i / 40]) {
          if (l < 0 || l > 1) continue;
          const c = fromHsl(h0, s0, l);
          if (worst(c) >= need0 + 0.1) { found = c; break; }
        }
      }
      if (!found) return false;
      el.style.setProperty('color', `rgb(${found.r} ${found.g} ${found.b})`, 'important');
      return true;
    }
    const op = opacity(el);
    if (op < 0.05) return false;
    const fg = over({ ...fgRaw, a: fgRaw.a * op }, bg);
    const size = parseFloat(cs.fontSize);
    const bold = parseInt(cs.fontWeight, 10) >= 700;
    const need = size >= 24 || (size >= 18.66 && bold) ? 3 : 4.5;
    if (ratio(fg, bg) >= need + 0.05) return false;
    const [h, s, l] = toHsl(fg);
    const lighter = lum(bg) < 0.4;
    let lo = lighter ? l : 0, hi = lighter ? 1 : l, best = null;
    for (let i = 0; i < 18; i++) {
      const mid = (lo + hi) / 2;
      const c = fromHsl(h, s, mid);
      if (ratio(c, bg) >= need + 0.1) { best = c; if (lighter) hi = mid; else lo = mid; } else if (lighter) lo = mid; else hi = mid;
    }
    if (!best) {
      // Even pure white/black text fails: the background is the problem. Move that solid surface along its hue.
      const holder = backgroundElement(el);
      const bgc = holder && parse(getComputedStyle(holder).backgroundColor);
      if (holder && holder !== root && bgc) {
        const [bh, bs, bl] = toHsl(bgc);
        const textLight = lum(fg) > lum(bg);
        // Translucent layers between the text and the surface being moved still tint the result.
        const above = [];
        for (let n = el; n && n !== holder; n = n.parentElement) {
          const lc = parse(getComputedStyle(n).backgroundColor);
          if (lc && lc.a > 0 && paintsUnder(n, el)) above.push(lc);
        }
        const tinted = (c) => above.reduceRight((base, layer) => over(layer, base), c);
        let blo = textLight ? 0 : bl, bhi = textLight ? bl : 1, bestBg = null;
        for (let i = 0; i < 18; i++) {
          const mid = (blo + bhi) / 2;
          const c = fromHsl(bh, bs, mid);
          if (ratio(fg, tinted(c)) >= need + 0.1) { bestBg = c; if (textLight) blo = mid; else bhi = mid; } else if (textLight) bhi = mid; else blo = mid;
        }
        if (bestBg) { holder.style.setProperty('background-color', `rgb(${bestBg.r} ${bestBg.g} ${bestBg.b})`, 'important'); return true; }
      }
      best = lighter ? { r: 255, g: 255, b: 255, a: 1 } : { r: 0, g: 0, b: 0, a: 1 };
    }
    el.style.setProperty('color', `rgb(${best.r} ${best.g} ${best.b})`, 'important');
    if (op < 1) el.style.setProperty('opacity', '1', 'important');
    return true;
  };

  const run = () => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (t) => (t.nodeValue.trim() && !/^(SCRIPT|STYLE|NOSCRIPT|svg|SVG)$/.test(t.parentElement?.tagName ?? '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
    });
    const els = [];
    const seen = new Set();
    for (let t = walker.nextNode(); t; t = walker.nextNode()) {
      const el = t.parentElement;
      if (!el || seen.has(el) || el.closest('svg')) continue;
      seen.add(el);
      els.push(el);
    }
    // Transitions would make getComputedStyle report mid-animation colours right after a change, so freeze them while measuring.
    const freeze = document.createElement('style');
    freeze.textContent = '.blog-post-content, .blog-post-content * { transition: none !important; animation-play-state: paused !important; }';
    document.head.appendChild(freeze);
    // Second pass: a surface adjusted for one element can change the pairs of its siblings.
    for (let pass = 0; pass < 3; pass++) for (const el of els) { try { fix(el); } catch (e) { /* leave this element as authored */ } }
    requestAnimationFrame(() => freeze.remove());
  };

  if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2500 });
  else setTimeout(run, 400);
})();
